// Libraries
import { Card, Center, Flex, Link, Text, useToast } from '@chakra-ui/react';
import NextLink from 'next/link';
import { useEffect, useState } from 'react';
// Components
import { EnvelopeIcon, FormSubmit } from '@atoms/index';
import { useCountdown, useSendResetPassword } from '@hooks/index';
import { ForgotPasswordForm } from '@organisms/index';
// Data
import { stateMachine } from './ForgotPasswordCard.state';
// Utils
import { AppPath, resetPasswordWaitTime } from '@constants/index';
import text from '@translations/en.json';

// Types
import { ForgotPasswordStatus } from './ForgotPasswordCard.type';

/**
 * ForgotPasswordCard component represents that represents a forgot password card.
 * @returns {ReactElement} The ForgotPasswordCard component.
 */
export const ForgotPasswordCard = () => {
  const { sendResetPasswordEmail, getTimeToNextMail } = useSendResetPassword();
  const { timeLeft, isRunning, start } = useCountdown(resetPasswordWaitTime);
  const [username, setUsername] = useState('');
  const [status, setStatus] = useState(ForgotPasswordStatus.INITIAL);
  const toast = useToast();
  const showForm = status === ForgotPasswordStatus.INITIAL;
  const iconH = showForm ? 8 : 11;
  const iconW = showForm ? 12 : 14;
  const timeToNextMail = getTimeToNextMail(username);
  const retryButtonText = isRunning ? `${text.retry_in} ${timeLeft} ${text.seconds}` : text.resend_code;

  /**
   * Handles the click event for the "Forgot Password" action.
   * Displays a success toast notification and sends a reset password email.
   */
  const handleClick = () => {
    toast({
      title: text.forgot_password_toast_title,
      description: text.forgot_password_toast_message,
      status: 'success',
    });
    sendResetPasswordEmail(username);
  };

  /**
   * This effect is responsible for starting the countdown for users when the form is not shown ant have timeToNextMail.
   */
  useEffect(() => {
    if (!showForm && timeToNextMail) {
      start(timeToNextMail);
    }
  }, [timeToNextMail, showForm, start]);

  return (
    <Card p={8} align='center' gap={8} maxW='fit-content' data-testid='forgot-password-card'>
      <Center h={10}>
        <EnvelopeIcon w={iconW} h={iconH} iconType={stateMachine[status].envelopeType} />
      </Center>
      <Text fontSize='xl' fontWeight='medium'>
        {stateMachine[status].title}
      </Text>
      <Text fontSize='m' h={15} maxW={72} textAlign='center'>
        {stateMachine[status].message(username)}
      </Text>
      {showForm ? (
        <ForgotPasswordForm setStatus={setStatus} setUsername={setUsername} />
      ) : (
        <>
          <Text fontSize='m' h={30} maxW={72} variant='faded' textAlign='center'>
            {stateMachine[status].confirmation}
          </Text>
          <FormSubmit onClick={handleClick} isDisabled={isRunning}>
            {retryButtonText}
          </FormSubmit>
        </>
      )}
      <Flex pt={5} w='full' borderTop='section'>
        <NextLink href={AppPath.LOGIN} passHref legacyBehavior>
          <Link variant='forms' px={1} ml={-1}>
            {text.return_to_login}
          </Link>
        </NextLink>
      </Flex>
    </Card>
  );
};
